import ky from 'ky';

interface AuthRequest {
  accessToken: string;
  teamDomain: string;
}

interface TeamData {
  id: string;
  type: string;
  attributes: {
    id: number;
    name: string;
    email_domain: string;
    domain: string;
    user: {
      id: number;
      email: string;
      firstname: string;
      lastname: string;
    }
    use_self_signup: boolean;
    signed_up: boolean;
    logo_urls: {
      thumb: string;
      original: string;
    }
    sign_in_methods: string[];
    locale: string;
    sso_forced_email_domains: string[];
  }
}

interface TeamResponse {
  result: {
    data: TeamData
  }
}

interface TeamListResponse {
  result: {
    data: TeamData[]
  }
}

interface SessionResponse {
  result: {
    data: {
      id: string;
      type: string;
      attributes: {
        user: {
          id: string
          attributes: {
            firstname: string;
            lastname: string;
            crn: string;
            email: string;
            state: string;
            id: number;
          }
        },
        team: {
          id: string;
          attributes: {
            domain: string;
            name: string;
            state: string;
            logo_urls: {
              thumb: string;
              original: string;
            },
            id: number;
            crn: string;
          }
        },
      }
    }
  }
}

export class CupixWorksApi {
  private static instance: CupixWorksApi;

  private static kyInstance() {
    return ky.create({
      timeout: 60000, // 60 seconds
      hooks: {
        beforeError: [
          error => {
            Promise.all([
              error.request?.text(),
              error.response?.text()
            ]).then(([requestBody, responseBody]) => {
              console.log(`req: ${requestBody}, rep: ${responseBody}`);
            });
            return error;
          }
        ],
        beforeRetry: [
          ({ request, options, error, retryCount }) => {
            console.log("trying to retry request: ", {
              url: request.url,
              retryCount: retryCount,
              message: error.message
            });
          }
        ]
      }
    });
  }

  private baseUrl: string;

  public constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  protected teamFields = [
    'id',
    'name',
    'user',
    'domain',
    'locale',
    'logo_urls',
    'signed_up',
    'email_domain',
    'use_self_signup',
    'sign_in_methods',
    'sso_forced_email_domains'
  ].join(',');

  public createTeam(params: { name: string; domain: string; email: string}) {
    return CupixWorksApi.kyInstance().post(`${this.baseUrl}/api/v1/teams/signup?fields=${this.teamFields}`, {
      json: {
        name: params.name,
        domain: params.domain,
        email: params.email,
      }
    })
    .json<TeamResponse>()
    .then((response) => response.result.data.attributes);
  }

  public updateTeam(params: { id: number; name: string; newDomain: string } & AuthRequest) {
    return CupixWorksApi.kyInstance().put(`${this.baseUrl}/api/v1/teams?fields=${this.teamFields}`, {
      headers: {
        'x-cupix-team-domain': params.teamDomain,
        'x-cupix-auth': params.accessToken,
      },
      json: {
        id: params.id,
        name: params.name,
        domain: params.newDomain,
      }
    })
    .json<TeamResponse>()
    .then((response) => response.result.data.attributes);
  }

  public inviteUsers(params: { emails: string[] } & AuthRequest) {
    return CupixWorksApi.kyInstance().post(`${this.baseUrl}/api/v1/teams/invitation`, {
      headers: {
        'x-cupix-team-domain': params.teamDomain,
        'x-cupix-auth': params.accessToken,
      },
      json: {
        user_emails: params.emails,
      }
    })
    .json();
  }

  public findTeamByDomain(domain: string) {
    return CupixWorksApi.kyInstance().get(`${this.baseUrl}/api/v1/teams/find_by_domain/${domain}?fields=${this.teamFields}`)
      .json<TeamResponse>()
      .then((response) => response.result.data.attributes);
  }

  public requestMyTeam(email: string) {
    const encodedEmail = encodeURIComponent(email.toLowerCase());
    return CupixWorksApi.kyInstance().get(`${this.baseUrl}/api/v1/accounts/request_my_teams?email=${encodedEmail}`).json();
  }

  public requestEmailConfirmation(email: string) {
    return CupixWorksApi.kyInstance().post(`${this.baseUrl}/api/v1/signups/request_email_confirmation`, {
      json: {
        email: email.toLowerCase(),
        name: email.toLowerCase(), // name is meaningless but tesla requires it.
      }
    },
    ).json();
  }

  public teamSignup(teamDomain: string, confirmationToken: string, email: string, firstname: string, lastname: string, password?: string) {
    return CupixWorksApi.kyInstance().post(`${this.baseUrl}/api/v1/signups/team`, {
      json: {
        team_domain: teamDomain,
        confirmation_token: confirmationToken,
        email: email.toLowerCase(),
        firstname: firstname,
        lastname: lastname,
        ...(password ? { password: password, password_confirmation: password } : {}),
      }
    },
    ).json();
  }

  public getSession(teamDomain: string, accessToken: string) {
    const fields = ["user", "team"].join(',');
    return CupixWorksApi.kyInstance().get(`${this.baseUrl}/api/v1/sessions?fields=${fields}`,
      {
        headers: {
          'x-cupix-team-domain': teamDomain,
          'x-cupix-auth': accessToken,
        }
      })
      .json<SessionResponse>()
      .then((response) => response.result.data.attributes);
  }

  public getTeams(accessToken: string) {
    return CupixWorksApi.kyInstance().get(`${this.baseUrl}/api/v1/accounts/teams?fields=${this.teamFields}`,
      {
        headers: {
          'x-cupix-auth': accessToken,
        }
      })
      .json<TeamListResponse>()
      .then((response) => response.result.data.map(data => data.attributes));
  }

  public updateUser(teamDomain: string, accessToken: string, firstname: string, lastname: string, updateState: boolean) {
    const fields = ["id", "firstname", "lastname", "state"];
    return CupixWorksApi.kyInstance().put(`${this.baseUrl}/api/v1/me?fields=${fields}`, {
      headers: {
        'x-cupix-team-domain': teamDomain,
        'x-cupix-auth': accessToken,
      },
      json: {
        firstname: firstname,
        lastname: lastname,
        ...(updateState ? {state: 'active'} : {})
      }
    },
    ).json();

  }

  public provisionUser(teamDomain: string, accessToken: string) {
    const fields = ["user", "team"].join(',');
    return CupixWorksApi.kyInstance().post(`${this.baseUrl}/api/v1/users/provision?fields=${fields}`, {
      headers: {
        'x-cupix-team-domain': teamDomain,
        'x-cupix-auth': accessToken,
      }
    })
    .json();
  }
}